import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import { useGame, usePlayer } from "@empirica/core/player/classic/react";
import { Button } from "../components/Button";
import { getCounterpartCopy } from "../payoffFrame";
import { startChatbotRequest } from "../../../shared/chatbotLatency.js";

const API_BASE = import.meta.env.VITE_LLM_PROXY_URL || "";
const API_URL = `${API_BASE}/api/llm`;
const MODELS_API_URL = `${API_BASE}/api/llm-models`;
const MODEL_LABELS = {
  "openai/gpt-5.6-terra": "GPT-5.6 Terra",
  "anthropic/claude-sonnet-5": "Claude Sonnet 5",
  "google/gemini-3.1-pro-preview": "Gemini 3.1 Pro Preview",
  "x-ai/grok-4.5": "Grok 4.5",
  "moonshotai/kimi-k3": "Kimi K3",
};

function formatModelLabel(modelId) {
  return MODEL_LABELS[modelId] || modelId;
}

function formatAssistantMarkdown(content) {
  if (!content) {
    return "";
  }

  const escapedCurrency = content.replace(
    /(?<!\$)\$(?!\$)(?=\s*\d)/g,
    "\\$"
  );

  return escapedCurrency
    .replace(/\\\[([\s\S]*?)\\\]/g, (_, expr) => `$$\n${expr}\n$$`)
    .replace(/\\\((.*?)\\\)/g, (_, expr) => `$${expr}$`)
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

const markdownComponents = {
  p: ({ children }) => <p className="my-2 leading-snug">{children}</p>,
  li: ({ children }) => <li className="leading-snug">{children}</li>,
  ul: ({ children }) => <ul className="my-2 pl-5 list-disc">{children}</ul>,
  ol: ({ children }) => <ol className="my-2 pl-5 list-decimal">{children}</ol>,
  blockquote: ({ children }) => (
    <blockquote className="my-2 pl-3 border-l-2 border-gray-300 text-gray-700 italic">
      {children}
    </blockquote>
  ),
  pre: ({ children }) => <pre className="my-2 whitespace-pre-wrap [overflow-wrap:anywhere]">{children}</pre>,
};

export function ChatbotChatStage({ chatMode = "negotiation" }) {
  const game = useGame();
  const player = usePlayer();
  const design = game.get("design") || {};
  const testMode = design.testMode === true;
  const llmModel =
    player.get("llmModel") || game.get("llmModel") || design.llmModel || "";
  const isFiller = chatMode === "filler";
  const counterpartLabel = getCounterpartCopy(design).term;

  const seededInitialMessage = useMemo(() => {
    const initialMessage = player.get("chatbotInitialMessage");
    return typeof initialMessage === "string" ? initialMessage.trim() : "";
  }, [player]);

  const initialMessages = useMemo(
    () => {
      const savedMessages = player.stage.get("aiChat");
      if (Array.isArray(savedMessages) && savedMessages.length > 0) {
        return savedMessages;
      }

      if (seededInitialMessage) {
        return [{ role: "user", content: seededInitialMessage }];
      }

      return [];
    },
    [player, seededInitialMessage]
  );

  const [messages, setMessages] = useState(initialMessages);
  const [input, setInput] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTestModel, setSelectedTestModel] = useState(() =>
    testMode ? player.stage.get("llmTestModel") || llmModel : llmModel
  );
  const [testModelOptions, setTestModelOptions] = useState(() =>
    llmModel ? [{ id: llmModel, usable: true }] : []
  );
  const [isLoadingModels, setIsLoadingModels] = useState(testMode);
  const [modelOptionsError, setModelOptionsError] = useState("");
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingText, setEditingText] = useState("");
  const messagesScrollerRef = useRef(null);
  const messageInputRef = useRef(null);
  const requestedInitialResponseRef = useRef(false);
  const activeLlmModel = testMode ? selectedTestModel || llmModel : llmModel;

  useEffect(() => {
    player.stage.set("chatMode", chatMode);
  }, [chatMode, player]);

  useEffect(() => {
    player.stage.set("aiChat", messages);
  }, [messages, player]);

  useEffect(() => {
    if (testMode && selectedTestModel) {
      player.stage.set("llmTestModel", selectedTestModel);
    }
  }, [player, selectedTestModel, testMode]);

  useEffect(() => {
    if (!testMode) {
      return undefined;
    }

    const abortController = new AbortController();
    setIsLoadingModels(true);
    setModelOptionsError("");

    fetch(MODELS_API_URL, { signal: abortController.signal })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data?.error || "Unable to load model options.");
        }

        const configuredModels = Array.isArray(data?.models)
          ? data.models.filter((model) => typeof model?.id === "string")
          : [];
        const preferredModels = configuredModels.filter(
          (model) => model.preferred === true
        );
        const usableModels = configuredModels.filter((model) => model.usable === true);
        const availableModels =
          preferredModels.length > 0 ? preferredModels : usableModels;

        if (availableModels.length === 0) {
          throw new Error("No configured models are currently available.");
        }

        setTestModelOptions(availableModels);
        setSelectedTestModel((currentModel) => {
          if (availableModels.some((model) => model.id === currentModel)) {
            return currentModel;
          }

          if (availableModels.some((model) => model.id === llmModel)) {
            return llmModel;
          }

          return availableModels[0].id;
        });
      })
      .catch((err) => {
        if (err.name !== "AbortError") {
          setModelOptionsError(err.message || "Unable to load model options.");
        }
      })
      .finally(() => {
        if (!abortController.signal.aborted) {
          setIsLoadingModels(false);
        }
      });

    return () => abortController.abort();
  }, [llmModel, testMode]);

  useEffect(() => {
    if (!messagesScrollerRef.current) {
      return;
    }

    messagesScrollerRef.current.scrollTop =
      messagesScrollerRef.current.scrollHeight;
  }, [messages.length]);

  useEffect(() => {
    if (
      requestedInitialResponseRef.current ||
      !activeLlmModel ||
      messages.length !== 1 ||
      messages[0]?.role !== "user" ||
      messages[0]?.content !== seededInitialMessage
    ) {
      return;
    }

    requestedInitialResponseRef.current = true;
    requestAssistant(messages, "initial").then((assistantMessage) => {
      if (assistantMessage) {
        setMessages([...messages, assistantMessage]);
      }
    });
  }, [activeLlmModel, messages, seededInitialMessage]);

  async function requestAssistant(nextMessages, requestType = "submit") {
    // Keep writes attached to this stage even if the participant advances while waiting.
    const requestStage = player.stage;
    const timing = startChatbotRequest({
      stage: requestStage,
      requestType,
      userMessageIndex: nextMessages.length - 1,
      requestedModel: activeLlmModel,
      chatMode,
    });
    setIsLoading(true);
    setError("");

    try {
      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: nextMessages, chatMode, model: activeLlmModel }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || "LLM request failed.");
      }

      const assistantText = String(data?.content || "").trim();
      if (!assistantText) {
        throw new Error("Empty chatbot response.");
      }

      const resolvedModel = typeof data?.model === "string" ? data.model : activeLlmModel;
      const requestedModel =
        typeof data?.requestedModel === "string" ? data.requestedModel : activeLlmModel;
      const fallbackModel =
        typeof data?.fallbackModel === "string" ? data.fallbackModel : null;

      timing.finish({ status: "completed", resolvedModel, fallbackModel });

      if (resolvedModel) {
        requestStage.set("llmResolvedModel", resolvedModel);
      }

      if (fallbackModel) {
        requestStage.set("llmModelFallback", {
          requestedModel,
          fallbackModel,
          at: new Date().toISOString(),
        });
      }

      return {
        role: "assistant",
        requestId: timing.requestId,
        content: assistantText,
        model: resolvedModel,
        requestedModel,
        fallbackModel,
      };
    } catch (err) {
      timing.finish({
        status: "failed",
        error: err.message || "Unable to reach chatbot.",
      });
      setError(err.message || "Unable to reach chatbot.");
      return null;
    } finally {
      setIsLoading(false);
    }
  }

  async function sendMessage(event) {
    event?.preventDefault();
    if (isLoading) {
      return;
    }

    const text = input.trim();
    if (!text) {
      return;
    }

    const nextMessages = [...messages, { role: "user", content: text }];
    setMessages(nextMessages);
    setInput("");

    const assistantMessage = await requestAssistant(nextMessages);
    if (assistantMessage) {
      setMessages([...nextMessages, assistantMessage]);
    }

    requestAnimationFrame(() => {
      messageInputRef.current?.focus();
    });
  }

  function startEditing(index) {
    if (isLoading) {
      return;
    }

    setEditingIndex(index);
    setEditingText(messages[index]?.content || "");
  }

  function cancelEditing() {
    setEditingIndex(null);
    setEditingText("");
  }

  async function saveEditedMessage(index) {
    if (isLoading) {
      return;
    }

    const trimmed = editingText.trim();
    if (!trimmed) {
      return;
    }

    const updatedMessages = messages.map((message, messageIndex) =>
      messageIndex === index ? { ...message, content: trimmed } : message
    );

    const baseMessages = updatedMessages.slice(0, index + 1);
    setMessages(baseMessages);
    cancelEditing();

    const assistantMessage = await requestAssistant(baseMessages, "edit");
    if (assistantMessage) {
      setMessages([...baseMessages, assistantMessage]);
    }
  }

  async function regenerateAssistant(index) {
    if (isLoading) {
      return;
    }

    let userIndex = -1;
    for (let i = index - 1; i >= 0; i -= 1) {
      if (messages[i]?.role === "user") {
        userIndex = i;
        break;
      }
    }

    if (userIndex === -1) {
      return;
    }

    const baseMessages = messages.slice(0, userIndex + 1);
    setMessages(baseMessages);

    const assistantMessage = await requestAssistant(baseMessages, "regenerate");
    if (assistantMessage) {
      setMessages([...baseMessages, assistantMessage]);
    }
  }

  return (
    <div className="w-full max-w-5xl mx-auto px-8 py-6">
      <div className="w-full rounded-md border border-gray-200 p-4 space-y-3">
        <div
          ref={messagesScrollerRef}
          className="max-h-[calc(100vh-18rem)] overflow-y-auto space-y-2"
        >
          <div className="rounded-md border border-empirica-200 bg-empirica-50 p-3">
            <h2 className="text-4xl font-semibold text-empirica-800">
              {isFiller ? "Chatbot Discussion" : "Chatbot Discussion"}
            </h2>
            <p className="text-lg text-empirica-700 mt-1">
              {isFiller
                ? "Discuss with a smart chatbot whether cats or dogs make better pets."
                : "Discuss your situation with a smart chatbot. "+
                  // "The chatbot does not know about the current situation, " +
                  // "so you need to provide any context you want it to know. "+
                  `Your ${counterpartLabel} will not see this conversation. ` +
                  "You are able to edit and regenerate messages."
              }
            </p>
            <p className="text-lg text-empirica-700 mt-2">
              Chatbot messages may take a few seconds to appear.
            </p>
            {testMode ? (
              <div className="mt-3 rounded-md border border-empirica-200 bg-white p-3">
                <label
                  htmlFor="test-llm-model"
                  className="block text-base font-semibold text-empirica-800"
                >
                  Model for the next response (test mode)
                </label>
                <select
                  id="test-llm-model"
                  className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-base text-gray-800 shadow-sm focus:border-empirica-500 focus:outline-none focus:ring-empirica-500"
                  value={activeLlmModel}
                  onChange={(event) => setSelectedTestModel(event.target.value)}
                  disabled={isLoading || isLoadingModels || testModelOptions.length === 0}
                >
                  {testModelOptions.map((model) => (
                    <option key={model.id} value={model.id}>
                      {formatModelLabel(model.id)}
                    </option>
                  ))}
                </select>
                {isLoadingModels ? (
                  <p className="mt-1 text-sm text-gray-500">Loading available models...</p>
                ) : null}
                {modelOptionsError ? (
                  <p className="mt-1 text-sm text-red-600">
                    {modelOptionsError} The selector is limited to the assigned model.
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>

          {messages.length === 0 ? (
            <p className="text-lg text-gray-500">No messages yet.</p>
          ) : (
            messages.map((message, index) => (
              <div
                key={`${message.role}-${index}`}
                className={`rounded-md p-3 text-lg break-words [overflow-wrap:anywhere] ${
                  message.role === "assistant"
                    ? "bg-gray-100 border border-gray-300 text-gray-800"
                    : "bg-empirica-100 border border-empirica-300 text-gray-800"
                }`}
              >
                <div className="mb-1 flex items-start justify-between gap-3">
                  <div className="text-base uppercase tracking-wide text-gray-400">
                    {message.role === "assistant"
                      ? testMode && message.model
                        ? `Chatbot · ${formatModelLabel(message.model)}`
                        : "Chatbot"
                      : "You"}
                  </div>
                  {message.role === "user" && editingIndex !== index ? (
                    <button
                      type="button"
                      className="text-xs font-medium text-empirica-700 hover:text-empirica-900"
                      onClick={() => startEditing(index)}
                      disabled={isLoading}
                    >
                      Edit
                    </button>
                  ) : null}
                  {message.role === "assistant" ? (
                    <button
                      type="button"
                      className="text-xs font-medium text-empirica-700 hover:text-empirica-900"
                      onClick={() => regenerateAssistant(index)}
                      disabled={isLoading}
                    >
                      Regenerate
                    </button>
                  ) : null}
                </div>
                {message.role === "user" && editingIndex === index ? (
                  <div className="space-y-2">
                    <textarea
                      className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-empirica-500 focus:border-empirica-500 text-lg"
                      rows={3}
                      value={editingText}
                      onChange={(e) => setEditingText(e.target.value)}
                      disabled={isLoading}
                    />
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        primary
                        disabled={isLoading || !editingText.trim()}
                        handleClick={() => saveEditedMessage(index)}
                      >
                        Save
                      </Button>
                      <Button
                        type="button"
                        disabled={isLoading}
                        handleClick={cancelEditing}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    {message.role === "assistant" ? (
                      <div className="[overflow-wrap:anywhere]">
                        <ReactMarkdown
                          remarkPlugins={[remarkMath, remarkGfm]}
                          rehypePlugins={[[rehypeKatex, { throwOnError: false, strict: false }]]}
                          components={markdownComponents}
                        >
                          {formatAssistantMarkdown(message.content)}
                        </ReactMarkdown>
                      </div>
                    ) : (
                      <div className="whitespace-pre-wrap">{message.content}</div>
                    )}
                  </>
                )}
              </div>
            ))
          )}
        </div>

        {error ? <p className="text-sm text-red-600">{error}</p> : null}

        <form className="space-y-2" onSubmit={sendMessage}>
          <textarea
            ref={messageInputRef}
            className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-empirica-500 focus:border-empirica-500 text-lg"
            rows={3}
            placeholder="Type a message..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={isLoading}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey && !event.isComposing) {
                event.preventDefault();
                sendMessage(event);
              }
            }}
          />
          <div className="flex items-center space-x-3">
            <Button type="submit" primary disabled={isLoading}>
              {isLoading ? "Sending..." : "Send"}
            </Button>
            {testMode ? (
              <Button
                type="button"
                disabled={player.stage.get("submit")}
                handleClick={() => player.stage.set("submit", true)}
              >
                End chat now (test)
              </Button>
            ) : null}
          </div>
        </form>
      </div>
    </div>
  );
}
